// ===== Proposta · Etapa 2 (fiscal e logística) =====
// Espelha as abas do Nomus EC: Transporte, Volumes, Valores acessórios,
// Local de retirada/entrega, Retenção de tributos e Exportação.
// Tributos calculados (ICMS/IPI/PIS/COFINS/ISSQN/IBS/CBS) NÃO são editados aqui —
// quem calcula é o Nomus na conversão da proposta em pedido.

import React from 'react';
import { Icon } from './icons.jsx';
import { FormField, Input, Select } from './modals.jsx';
import {
  INTEGRACOES_LOGISTICA, METODOS_RATEIO, SERVICOS_ENTREGA, TIPOS_REDESPACHO,
  TRANSPORTADORAS_CADASTRO, TRANSPORTADORA_BY_ID, TRANSPORTE_BY_ID, TRANSPORTE_MODALIDADES, UFS, brlExato,
} from './data.jsx';
import { CAIXAS_EMBALAGEM, CAIXA_BY_NOME, kgFmt, pesoLiquidoItens } from './pesos.jsx';
import { volumesSugeridos, cubagemDosVolumes, FATOR_CUBAGEM_KG_M3 } from './frete-caixas.js';

const { useState: useStateFis, useEffect: useEffectFis } = React;

function FisMoney({ value, onChange, readOnly }) {
  return (
    <div className="control mono" style={{ paddingLeft: 10, background: readOnly ? "var(--color-bg)" : undefined }}>
      <span style={{ fontSize: 12, color: "var(--color-ink-soft)", marginRight: 4 }}>R$</span>
      {readOnly
        ? <input readOnly value={Number(value || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} style={{ flex: 1, textAlign: "right" }} />
        : <input type="number" min="0" step="0.01" value={value} onChange={e => onChange(Math.max(0, Number(e.target.value) || 0))} style={{ flex: 1, textAlign: "right" }} />}
    </div>
  );
}

function FisNum({ value, onChange, unit, step = "0.001", readOnly }) {
  return (
    <div className="control mono" style={{ paddingLeft: 10, background: readOnly ? "var(--color-bg)" : undefined }}>
      <input type="number" min="0" step={step} value={value} readOnly={readOnly}
        onChange={e => onChange && onChange(Math.max(0, Number(e.target.value) || 0))} style={{ flex: 1, textAlign: "right" }} />
      {unit && <span style={{ fontSize: 11, color: "var(--color-ink-soft)", marginLeft: 4 }}>{unit}</span>}
    </div>
  );
}

function FisBlock({ title, hint, children, defaultOpen = true, badge }) {
  const [open, setOpen] = useStateFis(defaultOpen);
  return (
    <div className="fis-block">
      <button type="button" className="fis-h" onClick={() => setOpen(o => !o)}>
        <Icon name={open ? "chevron-down" : "chevron-right"} size={13} />
        <span className="fis-h-t">{title}</span>
        {hint && <span className="fis-h-hint">{hint}</span>}
        {badge && <span className="fis-h-badge">{badge}</span>}
      </button>
      {open && <div className="fis-body">{children}</div>}
    </div>
  );
}

// Nota explicativa que no celular fica em 1 linha até o toque em "ver mais" (spec 2026-10-15-proposta-clara-no-celular,
// itens 7 e 9). O texto inteiro fica sempre no DOM e o botão só aparece no celular: quem recolhe é o CSS. Só serve para
// nota explicativa; aviso (`.warn`, ICMS-ST, "Aviso impresso na proposta") nunca passa por aqui.
function NotaRecolhivel({ icon, style, children }) {
  const [aberta, setAberta] = useStateFis(false);
  return (
    <div className={"fis-note nota-recolhivel" + (aberta ? " aberta" : "")} style={style}>
      <Icon name={icon} size={12} />
      <span className="nota-recolhivel-txt">{children}</span>
      <button type="button" className="nota-recolhivel-tog" aria-expanded={aberta} onClick={() => setAberta(a => !a)}>
        {aberta ? "ver menos" : "ver mais"}
      </button>
    </div>
  );
}

// Campos raros do transporte. No desktop aparecem como sempre (o título fica escondido); no celular só o título,
// até o toque. Os campos ficam montados com o bloco fechado, para o que foi digitado não se perder.
function BlocoRaro({ title, children }) {
  const [aberto, setAberto] = useStateFis(false);
  return (
    <div className={"bloco-raro" + (aberto ? " aberto" : "")}>
      <button type="button" className="bloco-raro-tog" aria-expanded={aberto} onClick={() => setAberto(o => !o)}>
        <Icon name={aberto ? "chevron-down" : "chevron-right"} size={13} />
        <span>{title}</span>
      </button>
      {children}
    </div>
  );
}

// ─── Endereço alternativo (retirada / entrega) ────────────────────────────────
function FisEndereco({ end, onChange }) {
  const f = (k, v) => onChange({ ...end, [k]: v });
  return (
    <div className="fis-grid-4" style={{ marginTop: 8 }}>
      <FormField label="Logradouro" style={{ gridColumn: "span 2" }}><Input value={end.rua || ""} onChange={e => f("rua", e.target.value)} /></FormField>
      <FormField label="Número"><Input value={end.num || ""} onChange={e => f("num", e.target.value)} /></FormField>
      <FormField label="Bairro"><Input value={end.bairro || ""} onChange={e => f("bairro", e.target.value)} /></FormField>
      <FormField label="Cidade" style={{ gridColumn: "span 2" }}><Input value={end.cidade || ""} onChange={e => f("cidade", e.target.value)} /></FormField>
      <FormField label="UF"><Select value={end.uf || ""} onChange={v => f("uf", v)} options={[{ value: "", label: "Selecione" }, ...UFS.map(u => ({ value: u, label: u }))]} /></FormField>
      <FormField label="CEP"><Input value={end.cep || ""} onChange={e => f("cep", e.target.value)} placeholder="00000-000" /></FormField>
    </div>
  );
}

// ─── Etapa fiscal/logística completa ──────────────────────────────────────────
// `frete` e o frete COBRADO (base + margem). `freteBase` e `freteMargem` sao o MESMO estado do passo 1 (o construtor so tem um
// valor de frete): aqui e o segundo lugar para editar, junto da modalidade e da transportadora (spec 2026-10-08).
function PropostaFiscalStep({ fis, set, tvId, ambientes, itens, totalProd, frete, freteBase = 0, onFreteBase, freteMargem = 0, onFreteMargem }) {
  const modal      = TRANSPORTE_BY_ID[fis.modalidadeTransporte] || TRANSPORTE_MODALIDADES[0];
  const semFrete   = fis.modalidadeTransporte === 9;
  const exportacao = tvId === 60;
  const volumes    = fis.volumes || [];
  const acessorios = Number(fis.seguro || 0) + Number(fis.outrasDespesas || 0) + Number(frete || 0);
  const rateio     = METODOS_RATEIO.find(m => m.id === fis.metodoRateio) || METODOS_RATEIO[2];

  // Peso líquido dos itens pela calculadora PDLED (perfis e fitas por metro, fontes e presilhas por unidade)
  const calc       = pesoLiquidoItens(itens);
  const taraTotal  = volumes.reduce((s, v) => s + (Number(v.qtd) || 0) * ((CAIXA_BY_NOME[v.especie] || {}).tara || 0), 0);
  const brutoCalc  = Math.round((calc.kg + taraTotal) * 1000) / 1000;
  const pesoBruto  = fis.calcularVolumes ? brutoCalc : fis.pesoBruto;
  const pesoLiq    = fis.calcularVolumes ? calc.kg : fis.pesoLiquido;

  // Autopreenchimento: o serviço de entrega acompanha a modalidade de transporte
  // (sem frete = retirada no CD; com frete = entrega padrão), sempre editável.
  useEffectFis(() => {
    if (semFrete && fis.servicoEntrega !== "Retirada no CD") set({ servicoEntrega: "Retirada no CD" });
    if (!semFrete && (!fis.servicoEntrega || fis.servicoEntrega === "Retirada no CD")) set({ servicoEntrega: SERVICOS_ENTREGA[0] });
  }, [semFrete]);

  const updVol = (id, patch) => set({ volumes: volumes.map(v => v.id === id ? { ...v, ...patch } : v) });  const delVol = (id) => set({ volumes: volumes.filter(v => v.id !== id) });
  const addVol = () => set({ volumes: [...volumes, { id: "vol_" + Date.now().toString(36), qtd: 1, especie: "1M", marca: "PDLED", numeracao: "", pesoBruto: 0, pesoLiquido: 0 }] });
  // UMA linha por CAIXA, pelas regras da expedicao (src/frete-caixas.js: 17 kg e metros por caixa, barras de ate 3 m,
  // fonte ate 4 kg junto). Antes era um volume por ambiente, que ignorava os dois limites. Item sem peso fica fora e o
  // aviso "item sem peso" abaixo continua valendo -- nada de volume inventado. Spec 2026-10-07-volumes-e-peso-cubado.
  const sugerir = () => {
    set({ calcularVolumes: true, volumes: volumesSugeridos(itens).volumes });
  };
  const cub = cubagemDosVolumes(volumes);

  // Bloco de volumes: vem ANTES do Transporte e abre aberto, porque o frete depende do peso e da cubagem
  // (spec 2026-10-16-volumes-antes-do-frete, v2). Mesmo estado `fis`, so mudou o lugar onde desenha.
  const blocoVolumes = (
        <FisBlock title="Volumes transportados" defaultOpen>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <button className="btn sm" onClick={sugerir}><Icon name="package" size={12} /> Sugerir volumes</button>
            <button className="btn sm" onClick={addVol}><Icon name="plus" size={12} /> Criar volume</button>
            <label className="check-row" style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 12, marginLeft: "auto" }}>
              <input type="checkbox" checked={!!fis.calcularVolumes} onChange={e => set({ calcularVolumes: e.target.checked })} />
              <span>Calcular pelos pesos do catálogo</span>
            </label>
          </div>
          {volumes.length > 0 && (
            <div className="fis-vol-tbl" style={{ marginTop: 10 }}>
              <div className="fis-vol-row head">
                <div>Qtd</div><div>Caixa</div><div>Marca</div><div>Numeração</div><div>Peso líq. un.</div><div>Peso bruto un.</div><div></div>
              </div>
              {volumes.map(v => {
                const cx = CAIXA_BY_NOME[v.especie];
                const bruto = fis.calcularVolumes ? Math.round(((Number(v.pesoLiquido) || 0) + (cx ? cx.tara : 0)) * 1000) / 1000 : v.pesoBruto;
                return (
                  <div className="fis-vol-row" key={v.id}>
                    <FisNum value={v.qtd} step="1" onChange={n => updVol(v.id, { qtd: n })} />
                    <Select value={v.especie || ""} onChange={val => updVol(v.id, { especie: val })}
                      options={[{ value: "", label: "Outra" }, ...CAIXAS_EMBALAGEM.map(c => ({ value: c.nome, label: `${c.nome} · tara ${kgFmt(c.tara)} kg` }))]} />
                    <Input value={v.marca || ""} onChange={e => updVol(v.id, { marca: e.target.value })} />
                    <Input value={v.numeracao || ""} onChange={e => updVol(v.id, { numeracao: e.target.value })} />
                    <FisNum value={v.pesoLiquido} unit="kg" onChange={n => updVol(v.id, { pesoLiquido: n })} />
                    <FisNum value={bruto} unit="kg" readOnly={fis.calcularVolumes} onChange={n => updVol(v.id, { pesoBruto: n })} />
                    <button className="icon-btn" onClick={() => delVol(v.id)} style={{ opacity: 0.4 }}><Icon name="x" size={12} /></button>
                  </div>
                );
              })}
            </div>
          )}
          <div className="fis-grid-3" style={{ marginTop: 10 }}>
            <FormField label="Peso líquido total">
              <FisNum value={pesoLiq} unit="kg" readOnly={fis.calcularVolumes} onChange={n => set({ pesoLiquido: n })} />
            </FormField>
            <FormField label="Peso bruto total">
              <FisNum value={pesoBruto} unit="kg" readOnly={fis.calcularVolumes} onChange={n => set({ pesoBruto: n })} />
            </FormField>
          </div>
          {fis.calcularVolumes && (itens || []).length > 0 && (
            <div className="fis-note" style={{ marginTop: 8 }}>
              <Icon name="package" size={12} />
              <span>Itens {kgFmt(calc.kg)} kg{Object.keys(calc.por).length ? ` (${Object.entries(calc.por).map(([k, v]) => k + " " + kgFmt(v)).join(" · ")})` : ""} + tara das caixas {kgFmt(taraTotal)} kg = <strong>{kgFmt(brutoCalc)} kg brutos</strong>. Base: calculadora de peso e volumes PDLED.</span>
            </div>
          )}
          {cub && (
            <div className="fis-note" style={{ marginTop: 8 }} data-info="cubagem">
              <Icon name="package" size={12} />
              <span>{`Cubagem dos volumes: ${cub.metragemCubica.toLocaleString("pt-BR", { maximumFractionDigits: 4 })} m³ · peso cubado ${kgFmt(cub.pesoCubado)} kg (fator ${FATOR_CUBAGEM_KG_M3} kg/m³).`}</span>
            </div>
          )}
          {calc.sem.length > 0 && (
            <div className="fis-note warn" style={{ marginTop: 8 }}>
              <Icon name="alert-triangle" size={12} />
              <span>{calc.sem.length} {calc.sem.length === 1 ? "item sem peso na calculadora" : "itens sem peso na calculadora"} — informe o peso desses volumes à mão: {calc.sem.slice(0, 4).map(it => it.codigo || it.nome).join(", ")}{calc.sem.length > 4 ? ` e mais ${calc.sem.length - 4}` : ""}.</span>
            </div>
          )}
        </FisBlock>
  );

  return (
    <>
      <NotaRecolhivel icon="info">
        <strong>Tributos são calculados pelo Nomus.</strong> ICMS, ST, FCP, IPI, PIS, COFINS, ISSQN e IBS/CBS vêm preenchidos no pedido depois do envio — o CRM só informa o que o vendedor decide. O ICMS-ST que aparece nos Totais da proposta é uma estimativa; o valor definitivo é o da nota fiscal.
      </NotaRecolhivel>

      {blocoVolumes}

      {/* Transporte */}
      <FisBlock title="Transporte">
        <div className="fis-grid-3">
          <FormField label="Modalidade de transporte">
            <Select value={String(fis.modalidadeTransporte)} onChange={v => set({ modalidadeTransporte: Number(v) })}
              options={TRANSPORTE_MODALIDADES.map(t => ({ value: String(t.id), label: `${t.id}) ${t.nome}` }))} />
          </FormField>
          <FormField label="Transportadora">
            {TRANSPORTADORAS_CADASTRO.length
              ? <Select value={String(fis.idPessoaTransportadora ?? "")} onChange={v => { const t = TRANSPORTADORA_BY_ID[Number(v)]; set({ idPessoaTransportadora: t ? t.id : null, transportadora: t ? t.nome : "" }); }}
                  options={[{ value: "", label: "Selecione" }, ...TRANSPORTADORAS_CADASTRO.map(t => ({ value: String(t.id), label: t.nome }))]} />
              : <Input icon="truck" value={fis.transportadora} onChange={e => set({ transportadora: e.target.value })} placeholder="Digite o nome da transportadora" disabled={semFrete} />}
          </FormField>
          <FormField label="Serviço de entrega padrão">
            <Select value={fis.servicoEntrega} onChange={v => set({ servicoEntrega: v })}
              options={[{ value: "", label: "Selecione" }, ...SERVICOS_ENTREGA.map(s => ({ value: s, label: s }))]} />
          </FormField>
        </div>
        <BlocoRaro title="Redespacho e integração">
          <div className="fis-grid-3 bloco-raro-corpo" style={{ marginTop: 8 }}>
            <FormField label="Transportadora de redespacho">
              <Input value={fis.transportadoraRedespacho} onChange={e => set({ transportadoraRedespacho: e.target.value })} placeholder="opcional" />
            </FormField>
            <FormField label="Tipo de redespacho">
              <Select value={fis.tipoRedespacho} onChange={v => set({ tipoRedespacho: v })}
                options={TIPOS_REDESPACHO.map(t => ({ value: t, label: t }))} />
            </FormField>
            <FormField label="Integração logística">
              <Select value={fis.integracaoLogistica} onChange={v => set({ integracaoLogistica: v })}
                options={INTEGRACOES_LOGISTICA.map(t => ({ value: t, label: t }))} />
            </FormField>
          </div>
        </BlocoRaro>
        {/* O cliente pediu OUTRA transportadora ou modalidade? O valor do frete combinado se indica aqui, junto da escolha. */}
        <div className="fis-grid-3" style={{ marginTop: 8 }}>
          <FormField label="Valor do frete">
            <FisMoney value={freteBase} onChange={n => onFreteBase && onFreteBase(n)} />
          </FormField>
          <FormField label="Margem sobre o frete" hint="interno">
            <FisMoney value={freteMargem} onChange={n => onFreteMargem && onFreteMargem(n)} />
          </FormField>
          <FormField label="Frete cobrado">
            <FisMoney value={frete} readOnly />
          </FormField>
        </div>
        {freteMargem > 0 && (
          <div data-info="frete-cobrado" style={{ fontSize: 11, color: "var(--color-ink-faint)", margin: "4px 0 0" }}>
            Frete cobrado {brlExato(frete)} = frete {brlExato(freteBase)} + margem {brlExato(freteMargem)}. A margem é interna: o cliente vê só o frete cobrado.
          </div>
        )}
        <NotaRecolhivel icon="truck" style={{ marginTop: 6 }}>
          Se o cliente pediu outra transportadora ou outra modalidade, escolha-as acima e indique aqui o valor do frete combinado. A margem é opcional e interna: o cliente vê só o frete cobrado.
        </NotaRecolhivel>
        {semFrete && Number(frete) > 0 && (
          <div className="fis-note warn" style={{ marginTop: 6 }}>
            <Icon name="alert-triangle" size={12} />
            <span>{`Modalidade sem transporte, mas a proposta tem frete de R$ ${Number(frete).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}. Confira a modalidade ou zere o frete.`}</span>
          </div>
        )}
        <FormField label="Tipo de rastreio de entrega" style={{ marginTop: 8, maxWidth: 300 }}>
          <div className="control" style={{ background: "var(--color-bg)", color: "var(--color-ink-faint)", fontSize: 12 }}>
            {fis.integracaoLogistica && fis.integracaoLogistica !== "Nenhuma" ? "Automático · " + fis.integracaoLogistica : "Sem rastreio automático"}
          </div>
        </FormField>
      </FisBlock>

      {/* Valores acessórios */}
      <FisBlock title="Valores acessórios e rateio">
        <div className="fis-grid-3">
          <FormField label="Valor total do frete">
            <FisMoney value={frete} readOnly />
          </FormField>
          <FormField label="Valor total do seguro">
            <FisMoney value={fis.seguro} onChange={n => set({ seguro: n })} />
          </FormField>
          <FormField label="Outras despesas acessórias">
            <FisMoney value={fis.outrasDespesas} onChange={n => set({ outrasDespesas: n })} />
          </FormField>
        </div>
        <FormField label="Método para rateio de valores para itens da proposta" style={{ marginTop: 8 }}>
          <Select value={String(fis.metodoRateio)} onChange={v => set({ metodoRateio: Number(v) })}
            options={METODOS_RATEIO.map(m => ({ value: String(m.id), label: m.nome }))} />
        </FormField>
      </FisBlock>

      {/* Local de retirada / entrega */}
      <FisBlock title="Local de retirada e entrega" defaultOpen={false}>
        <label className="check-row" style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5 }}>
          <input type="checkbox" checked={!!fis.retiradaDiferente} onChange={e => set({ retiradaDiferente: e.target.checked })} />
          <span>Local de retirada diferente do endereço do remetente</span>
        </label>
        {fis.retiradaDiferente && <FisEndereco end={fis.retirada || {}} onChange={v => set({ retirada: v })} />}
        <label className="check-row" style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, marginTop: 10 }}>
          <input type="checkbox" checked={!!fis.entregaDiferente} onChange={e => set({ entregaDiferente: e.target.checked })} />
          <span>Local de entrega diferente do endereço do destinatário</span>
        </label>
        {fis.entregaDiferente && <FisEndereco end={fis.entrega || {}} onChange={v => set({ entrega: v })} />}
      </FisBlock>

      {/* Retenção de tributos — REMOVIDA em 16/09.
          Eram sete campos manuais (PIS, COFINS, CSLL, base e valor de IRRF,
          base e valor da Previdência) que o vendedor preenchia à mão.

          NÃO EXISTE RETENÇÃO SOBRE VENDA DE MERCADORIA. A regra vem do art. 30
          da Lei 10.833/2003, e ele alcança uma LISTA DE SERVIÇOS — limpeza,
          conservação, manutenção, segurança, vigilância, transporte de valores,
          locação de mão-de-obra, assessoria e serviços profissionais. Venda de
          luminária não está nela.

          O Kaio confirmou em 16/09 que as propostas da PDLED não têm parcela de
          serviço. Sem serviço, não há o que reter — e campo que ninguém deve
          preencher é convite a preencher errado, ainda mais sete deles numa
          tela de imposto.

          Se um dia houver instalação ou projeto destacado na proposta, a regra
          alcança ESSA parcela e os campos voltam, calculados sobre ela — não
          sobre o total. */}

      {/* Exportação — só em VENDAS EXTERIOR */}
      {exportacao && (
        <FisBlock title="Exportação">
          <div className="fis-grid-3">
            <FormField label="Local de embarque"><Input value={fis.exportacao.localEmbarque} onChange={e => set({ exportacao: { ...fis.exportacao, localEmbarque: e.target.value } })} /></FormField>
            <FormField label="UF do local de embarque">
              <Select value={fis.exportacao.ufEmbarque} onChange={v => set({ exportacao: { ...fis.exportacao, ufEmbarque: v } })}
                options={[{ value: "", label: "Selecione" }, ...UFS.map(u => ({ value: u, label: u }))]} />
            </FormField>
            <FormField label="Local de despacho"><Input value={fis.exportacao.localDespacho} onChange={e => set({ exportacao: { ...fis.exportacao, localDespacho: e.target.value } })} /></FormField>
          </div>
        </FisBlock>
      )}
    </>
  );
}

// Estado inicial da etapa fiscal a partir de uma proposta existente (ou vazia)
function propostaFiscalInicial(pb = {}, sug = {}) {
  const f = pb.fiscal || {};
  const modalidade = f.modalidadeTransporte ?? pb.modalidadeTransporte ?? 0;
  return {
    modalidadeTransporte: modalidade,
    // A proposta gravada (0096) manda; a sugestao do historico do cliente so entra quando a proposta nao diz nada.
    transportadora: f.transportadora ?? pb.transportadora ?? sug.transportadora ?? "",
    idPessoaTransportadora: f.idPessoaTransportadora ?? pb.idPessoaTransportadora ?? sug.idPessoaTransportadora ?? null,
    servicoEntrega: f.servicoEntrega ?? sug.servicoEntrega ?? (modalidade === 9 ? "Retirada no CD" : SERVICOS_ENTREGA[0]),
    transportadoraRedespacho: f.transportadoraRedespacho ?? "",
    tipoRedespacho: f.tipoRedespacho ?? TIPOS_REDESPACHO[0],
    integracaoLogistica: f.integracaoLogistica ?? INTEGRACOES_LOGISTICA[0],
    calcularVolumes: f.calcularVolumes !== false,
    volumes: f.volumes || [],
    pesoBruto: f.pesoBruto || 0,
    pesoLiquido: f.pesoLiquido || 0,
    seguro: f.seguro || 0,
    outrasDespesas: f.outrasDespesas || 0,
    metodoRateio: f.metodoRateio ?? 2,
    retiradaDiferente: !!f.retiradaDiferente,
    retirada: f.retirada || {},
    entregaDiferente: !!f.entregaDiferente,
    entrega: f.entrega || {},
    // `retencao` saiu em 16/09 junto com os campos da tela: nao existe retencao
    // sobre venda de mercadoria (art. 30 da Lei 10.833/2003 e lista de
    // SERVICOS), e nada lia este objeto -- nem o payload do ERP. Estado morto
    // que ninguem le confunde quem vier depois.
    exportacao: f.exportacao || { localEmbarque: "", ufEmbarque: "", localDespacho: "" },
  };
}

export { PropostaFiscalStep, propostaFiscalInicial, propostaFiscalSugerida, FisBlock, FisMoney, FisNum };

// Sugestão de logística a partir da última proposta do mesmo cliente — transportadora
// e serviço de entrega já vêm preenchidos quando o cliente tem histórico.
function propostaFiscalSugerida(store, lead) {
  try {
    if (!store || !lead) return {};
    const leads = lead.clienteId ? (store.getLeadsByCliente(lead.clienteId) || [lead]) : [lead];
    const props = leads.flatMap(l => store.getPropostasByLead(l.id) || []);
    const fontes = props.map(p => p.fiscal || p)
      .filter(x => x && (x.idPessoaTransportadora || x.transportadora || x.servicoEntrega));
    const u = fontes[fontes.length - 1];
    if (!u) return {};
    return {
      transportadora: u.transportadora || "",
      idPessoaTransportadora: u.idPessoaTransportadora ?? null,
      servicoEntrega: u.servicoEntrega || "",
    };
  } catch { return {}; }
}
